import { featureFlagsQuery, isFeatureEnabled } from "@common/api/feature-flags"
import { TracesService } from "@common/client"
import { SQUAWK_CARD } from "@common/flags"
import {
  type ChatFeature,
  type ChatMessage,
  type InputMetadata,
  type Subagent,
  useChat,
} from "@common/hooks/use-chat"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { isCards } from "@common/lib/chat-guards"
import type { UIState } from "@common/lib/ui-state"
import { useQueryClient } from "@tanstack/react-query"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

const CHAT_FEATURES: ChatFeature[] = [
  "sandbox",
  "thinking",
  "progress",
  "title",
  "refetch",
]

const explode = { throwOnError: true } as const

async function linkWorkTask(traceId: string, workTaskId?: string) {
  const work_task_ids = workTaskId ? [workTaskId] : []
  const req = { trace_id: traceId, traceWorkTasksUpdate: { work_task_ids } }
  await TracesService.setTraceWorkTasksEndpoint(req, explode)
}

async function createNewTrace(workTaskId?: string): Promise<string> {
  const { data } = await TracesService.createDraftTrace({}, explode)
  await linkWorkTask(data.id, workTaskId)
  return data.id
}

interface TraceContextValue {
  activeId: string | null
  ensureTrace: (
    entryPoint?: "composer" | "attachment" | "connection",
  ) => Promise<string>
  ensureConnection: () => Promise<void>
  adoptTrace: (id: string, entryPoint?: "history" | "deep_link") => void
  getFeatures: () => Promise<ChatFeature[]>
  sendMessage: (
    text: string,
    modeId?: string,
    metadata?: InputMetadata,
  ) => Promise<string>
  reset: () => void
  stop: () => void
  connected: boolean
  setMustRecreate: (value: boolean) => void
  messages: ChatMessage[]
  thinking: boolean
  progress: string | null
  subagents: Record<string, Subagent>
  onSend: (listener: () => void) => () => void
}

const TraceContext = createContext<TraceContextValue | null>(null)

/**
 * This handles setting a trace session per work task
 * it does not reset anything when work task changes so expects a key
 */
export function TraceProvider({
  workTaskId,
  children,
  getUIState,
  fromTraceId,
}: {
  workTaskId?: string
  children: ReactNode
  getUIState?: () => UIState
  fromTraceId?: string | null
}) {
  const tracePromiseRef = useRef<Promise<string> | null>(null)
  const activeIdRef = useRef<string | null>(null)
  const mustRecreateRef = useRef(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const chat = useChat()
  const trackEvent = useTrackEvent()
  const sendListenersRef = useRef(new Set<() => void>())
  const responseRef = useRef<{
    started_at: number
    message_count: number
  } | null>(null)

  const onSend = useCallback((listener: () => void) => {
    const listeners = sendListenersRef.current
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const reset = useCallback(() => {
    tracePromiseRef.current = null
    activeIdRef.current = null
    responseRef.current = null
    setActiveId(null)
    chat.reset()
  }, [chat.reset])

  const setMustRecreate = useCallback((value: boolean) => {
    mustRecreateRef.current = value
  }, [])

  const ensureTrace = useCallback(
    (
      entryPoint: "composer" | "attachment" | "connection" = "connection",
    ): Promise<string> => {
      if (mustRecreateRef.current) {
        mustRecreateRef.current = false
        reset()
      }
      if (tracePromiseRef.current) return tracePromiseRef.current
      const pending = createNewTrace(workTaskId)
        .then((id) => {
          activeIdRef.current = id
          setActiveId(id)
          trackEvent("agent_session_started", {
            trace_id: id,
            entry_point: entryPoint,
            ...(workTaskId && { work_task_id: workTaskId }),
          })
          return id
        })
        .catch((error: unknown) => {
          if (tracePromiseRef.current === pending)
            tracePromiseRef.current = null
          throw error
        })
      tracePromiseRef.current = pending
      return pending
    },
    [workTaskId, reset, trackEvent],
  )

  const getFeatures = useCallback(async (): Promise<ChatFeature[]> => {
    const flags = await queryClient
      .ensureQueryData(featureFlagsQuery())
      .catch(() => null)
    const squawkCard = flags && isFeatureEnabled(flags, SQUAWK_CARD)
    return squawkCard ? [...CHAT_FEATURES, "squawk_tool"] : CHAT_FEATURES
  }, [queryClient])

  const sendMessage = useCallback(
    async (
      text: string,
      modeId?: string,
      metadata?: InputMetadata,
    ): Promise<string> => {
      for (const listener of sendListenersRef.current) listener()
      const id = await ensureTrace("composer")
      const features = await getFeatures()
      responseRef.current = {
        started_at: performance.now(),
        message_count: chat.messages.length,
      }
      try {
        await chat.sendMessage(id, text, {
          modeId,
          features,
          uiState: getUIState?.(),
          metadata,
        })
      } catch (error) {
        responseRef.current = null
        trackEvent("agent_response_failed", { trace_id: id, stage: "send" })
        throw error
      }
      return id
    },
    [
      chat.messages.length,
      chat.sendMessage,
      ensureTrace,
      getFeatures,
      getUIState,
      trackEvent,
    ],
  )

  useEffect(() => {
    const response = responseRef.current
    if (chat.thinking || !response || !activeIdRef.current) return
    responseRef.current = null
    const responseMessages = chat.messages.slice(response.message_count)
    trackEvent("agent_response_completed", {
      trace_id: activeIdRef.current,
      latency_ms: Math.round(performance.now() - response.started_at),
      tool_count: responseMessages.filter((message) => message.type === "tool")
        .length,
      card_count: responseMessages
        .filter(isCards)
        .reduce((total, message) => total + message.output.cards.length, 0),
    })
  }, [chat.messages, chat.thinking, trackEvent])

  const ensureConnection = useCallback(async () => {
    const id = await ensureTrace("connection")
    const features = await getFeatures()
    await chat.ensureConnection(id, features)
  }, [chat.ensureConnection, ensureTrace, getFeatures])

  const adoptTrace = useCallback(
    (id: string, entryPoint: "history" | "deep_link" = "history") => {
      const connect = () =>
        getFeatures()
          .then((features) => chat.ensureConnection(id, features))
          .catch(() => {})
      if (activeIdRef.current === id) {
        connect()
        return
      }
      chat.reset()
      responseRef.current = null
      activeIdRef.current = id
      setActiveId(id)
      tracePromiseRef.current = Promise.resolve(id)
      trackEvent("agent_session_resumed", {
        trace_id: id,
        entry_point: entryPoint,
      })
      if (workTaskId) {
        linkWorkTask(id, workTaskId)
          .catch(() => {})
          .then(connect)
      } else {
        connect()
      }
    },
    [workTaskId, chat.reset, chat.ensureConnection, getFeatures, trackEvent],
  )

  // Re-attach the trace session the extension passed through, whenever it changes.
  useEffect(() => {
    if (fromTraceId) adoptTrace(fromTraceId, "deep_link")
  }, [fromTraceId, adoptTrace])

  const stop = useCallback(() => {
    if (activeIdRef.current) {
      trackEvent("agent_response_stopped", { trace_id: activeIdRef.current })
    }
    responseRef.current = null
    chat.stop()
  }, [chat.stop, trackEvent])

  const value = useMemo(
    () => ({
      activeId,
      messages: chat.messages,
      thinking: chat.thinking,
      progress: chat.progress,
      subagents: chat.subagents,
      getFeatures,
      sendMessage,
      ensureTrace,
      ensureConnection,
      adoptTrace,
      reset,
      stop,
      connected: chat.connected,
      setMustRecreate,
      onSend,
    }),
    [
      activeId,
      chat.messages,
      chat.thinking,
      chat.progress,
      chat.subagents,
      stop,
      chat.connected,
      getFeatures,
      sendMessage,
      ensureTrace,
      ensureConnection,
      adoptTrace,
      reset,
      setMustRecreate,
      onSend,
    ],
  )

  return <TraceContext.Provider value={value}>{children}</TraceContext.Provider>
}

export function useTrace() {
  const ctx = useContext(TraceContext)
  if (!ctx) throw new Error("useTrace must be used within TraceProvider")
  return ctx
}
